import React, { useEffect, useRef, useState, useCallback } from "react";
import io from "socket.io-client";
import {
    Container,
    Grid,
    Box,
    TextField,
    List,
    ListItem,
    ListItemText,
    Button,
    Typography,
    Stack,
    Avatar,
    InputAdornment,
    IconButton,
    Tooltip,
    CircularProgress,
} from "@mui/material";
import { useSelector } from "react-redux";
import { capitalizeFirstLetter } from "../../utils/helpers";
import { Loader } from "../../components/CustomLoader";
import { IMAGE_BASE } from "../../utils/constants";
import { useMediaQuery } from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import AnnouncementIcon from '@mui/icons-material/Announcement';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import { useLocation } from "react-router-dom";

const SOCKET_SERVER_URL = "https://chatapp-api.devtechnosys.tech";

const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const seconds = Math.floor((new Date() - date) / 1000);
    if (seconds < 60) return "just now";
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
};

export default function SupportChat() {
    const [socket, setSocket] = useState(null);
    const [inbox, setInbox] = useState([]);
    const [inboxLoading, setInboxLoading] = useState(false);
    const [inboxPage, setInboxPage] = useState(1);
    const inboxLimit = 20;
    const [inboxTotalPages, setInboxTotalPages] = useState(1);
    const [selectedUser, setSelectedUser] = useState(null);
    const selectedUserRef = useRef(selectedUser);
    const [chatMessages, setChatMessages] = useState([]);
    const [chatLoading, setChatLoading] = useState(false);
    const [messageInput, setMessageInput] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [isBroadcastMode, setIsBroadcastMode] = useState(false);
    const [authError, setAuthError] = useState(false);

    // Audio recording states
    const [isRecording, setIsRecording] = useState(false);
    const [audioBlob, setAudioBlob] = useState(null);
    const [recordingTime, setRecordingTime] = useState(0);
    const [playingAudioId, setPlayingAudioId] = useState(null);
    const [audioProgress, setAudioProgress] = useState({});

    const chatBoxRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const recordingIntervalRef = useRef(null);
    const audioRefs = useRef({});

    const token = useSelector((state) => state.auth.token);
    const admin = useSelector((state) => state.auth?.user);
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));
     const location = useLocation();
  const { userId, user } = location.state || {};
  console.log("user",user);
  

    useEffect(() => {
    }, [token, admin]);

    useEffect(() => {
        selectedUserRef.current = selectedUser;
    }, [selectedUser]);

    useEffect(() => {
        if (chatBoxRef.current) {
            chatBoxRef.current.scrollTo({
                top: chatBoxRef.current.scrollHeight,
                behavior: "smooth",
            });
        }
    }, [chatMessages]);


useEffect(() => {
    if (user && inbox.length >= 0 && !selectedUser) {
        const matchedConv = inbox.find(
            (conv) => conv?.userInfo?._id === user._id
        );

        if (matchedConv) {
            handleUserSelect(matchedConv);
        } else {
            // User is new - not found in inbox
            setSelectedUser({
                userId: user._id,
                username: user.username || `${user.firstname} ${user.lastname || ""}`,
                firstname: user.firstname,
                lastname: user.lastname
            });

            // Fetch messages with new user
            fetchChatMessages(user._id);
        }
    }
}, [user, inbox, selectedUser]);



    const fetchInbox = useCallback(() => {
        if (!socket) return;
        setInboxLoading(true);

        socket.emit(
            "getsupportInbox",
            {
                searchTerm: searchTerm,
                page: inboxPage,
                limit: inboxLimit,
                receiverName: searchTerm
            },
            (response) => {

                const data = response?.data || [];

                const updatedInbox = data.map((conv) => ({
                    ...conv,
                    userInfo: conv.userDetails || conv.otherUserInfo || {},
                    lastMessage: conv.message,
                    timeAgo: formatTimeAgo(conv.createdAt || new Date()),
                }));

                setInbox(updatedInbox);
                setInboxTotalPages(response?.totalPages || 1);
                setInboxLoading(false);
            }
        );
    }, [socket, inboxPage, inboxLimit, searchTerm]);

    const fetchChatMessages = useCallback(
        (receiverId) => {
            if (!socket || !receiverId) return;
            setChatLoading(true);

            socket.emit(
                "getsupportChatHistory",
                { receiverId, page: 1, limit: 100 },
                (response) => {
                    const messages = response?.data || [];
                    const userInfo = response?.otherUserInfo || {};

                    setChatMessages(
                        messages.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
                    );
                    setSelectedUser({
                        userId: userInfo._id,
                        username: userInfo.username,
                        firstname: userInfo.firstname,
                        lastname: userInfo.lastname,
                        image: userInfo.image,
                    });
                    setChatLoading(false);
                }
            );
        },
        [socket]
    );


    useEffect(() => {
        if (!token) {
            setAuthError(true);
            return;
        }

        if (!admin || !admin._id) {
            setAuthError(true);
            return;
        }


        const newSocket = io(SOCKET_SERVER_URL, {
            transports: ["websocket"],
            auth: { token },
            timeout: 10000,
        });

        newSocket.on("connect", () => {
            console.log("✅ Connected to socket:", newSocket.id);
        });

        newSocket.on("connect_error", (err) => {
            console.error("❌ Error details:", err);

            if (err.message.includes("Authentication error") || err.message.includes("User not found")) {


                setAuthError(true);

            }
        });

        newSocket.on("disconnect", (reason) => {
            console.log("🔌 Socket disconnected:", reason);
        });

        newSocket.on("error", (error) => {
            console.error("❌ Socket error:", error);
        });

        setSocket(newSocket);

        return () => {
            console.log("🧹 Cleaning up socket connection");
            newSocket.disconnect();
        };
    }, [token]);

    const handleNewInboxMessage = (message) => {
        setInbox((prevInbox) => {
            const updated = [...prevInbox];
            const senderId = message.sender?.id;
            const receiverId = message.receiver?.id;

            const chatUserId =
                message.sender?.role === "admin" ? receiverId : senderId;

            const idx = updated.findIndex((c) => c.userInfo?._id === chatUserId);
            const isChatOpen = selectedUserRef.current?.userId === chatUserId;

            const timeAgo = formatTimeAgo(message.createdAt || new Date());

            const userInfo =
                message.sender?.role === "admin" ? message.receiver : message.sender;

            let lastMessage = "";
            if (message.audio) {
                lastMessage = "🎤 Voice message";
            } else if (message.message) {
                lastMessage = message.message;
            } else {
                lastMessage = "Message";
            }

            if (idx > -1) {
                const currentConv = updated[idx];
                updated[idx] = {
                    ...currentConv,
                    lastMessage,
                    createdAt: message.createdAt,
                    timeAgo,
                    unreadCount: isChatOpen ? 0 : (currentConv.unreadCount || 0) + 1,
                };
            } else {
                updated.unshift({
                    userInfo,
                    lastMessage,
                    createdAt: message.createdAt,
                    timeAgo,
                    unreadCount: isChatOpen ? 0 : 1,
                    _id: message._id || Date.now(),
                });
            }

            return [...updated];
        });
    };

    useEffect(() => {

        if (!socket) return;

        const handleReceiveMessage = (message) => {

            const selectedId = selectedUserRef.current?.userId?.toString();
            const senderId = message.sender?.id?.toString();
            const receiverId = message.receiver?.id?.toString();
            const isBroadcastMsg = message.broadcast || receiverId === "broadcast";

            if (isBroadcastMode && isBroadcastMsg) {
                setChatMessages((prevMessages) => {
                    const updatedMessages = [...prevMessages, message];
                    updatedMessages.sort(
                        (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
                    );
                    return updatedMessages;
                });
                return;
            }


            if (selectedId && (senderId === selectedId || receiverId === selectedId)) {
                setChatMessages((prevMessages) => {
                    const updatedMessages = [...prevMessages, message];
                    updatedMessages.sort(
                        (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
                    );
                    return updatedMessages;
                });
            }
        };

        const handleNewInboxMessageWrapper = (message) => {
            handleNewInboxMessage(message);
        };

        socket.on("receiveSupportMessage", handleReceiveMessage);
        socket.on("newsupportinboxMessage", handleNewInboxMessageWrapper);

        return () => {
            socket.off("receiveSupportMessage", handleReceiveMessage);
            socket.off("newsupportinboxMessage", handleNewInboxMessageWrapper);
            socket.off("receiveBroadcastMessage", handleReceiveMessage);
        };
    }, [socket]);
    console.log("socket out", socket);


    useEffect(() => {
        if (socket) fetchInbox();
    }, [socket, inboxPage, searchTerm, fetchInbox]);

    const handleUserSelect = (conv) => {
        const userInfo = conv.userInfo;

        if (isBroadcastMode) {
            setIsBroadcastMode(false);
        }

        setSelectedUser({ userId: userInfo._id, username: userInfo.username });
        fetchChatMessages(userInfo._id);

        setInbox((prev) =>
            prev.map((c) =>
                c.userInfo?._id === userInfo._id ? { ...c, unreadCount: 0 } : c
            )
        );
    };

    // Add this inside your component:

    useEffect(() => {
        // Find the latest audio message sent by ME (admin) and not already played
        if (chatMessages.length === 0) return;
        const lastMsg = chatMessages[chatMessages.length - 1];

        // Detect if it's a local temp sent message by me (customize as needed)
        const isTempAndMine = lastMsg.audio && lastMsg.sender?.role === "admin" && typeof lastMsg._id === "number";

        if (isTempAndMine && audioRefs.current[lastMsg._id]) {
            audioRefs.current[lastMsg._id].play();
            setPlayingAudioId(lastMsg._id);
        }
    }, [chatMessages]);


    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setInboxPage(1);
    };

    const toggleBroadcastMode = () => {
        if (isBroadcastMode) {
            setIsBroadcastMode(false);
        } else {
            setIsBroadcastMode(true);
            setSelectedUser(null);
            setChatMessages([]);
        }
    };

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mediaRecorder = new MediaRecorder(stream);
            mediaRecorderRef.current = mediaRecorder;
            audioChunksRef.current = [];
            mediaRecorder.ondataavailable = (event) => {
                audioChunksRef.current.push(event.data);
            };
            mediaRecorder.onstop = () => {
                const audioBlob = new Blob(audioChunksRef.current, { type: "audio/wav" });
                setAudioBlob(audioBlob);
                stream.getTracks().forEach(track => track.stop());
            };
            mediaRecorder.start();
            setIsRecording(true);
            setRecordingTime(0);
            recordingIntervalRef.current = setInterval(() => {
                setRecordingTime(prev => prev + 1);
            }, 1000);
        } catch (error) {
            setIsRecording(false);
            console.error("Error starting recording:", error);
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            clearInterval(recordingIntervalRef.current);
        }
    };

    const cancelRecording = () => {
        if (isRecording && mediaRecorderRef.current) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            clearInterval(recordingIntervalRef.current);
        }
        setAudioBlob(null);
        setRecordingTime(0);
    };


    const base64ToBlobUrl = (base64Data, mimeType = "audio/wav") => {
        if (!base64Data) return null;
        // Remove data url prefix if present
        const cleanedBase64 = base64Data.replace(/^data:audio\/[a-zA-Z]+;base64,/, "");
        // Replace url-safe base64 chars
        let base64 = cleanedBase64.replace(/-/g, "+").replace(/_/g, "/");

        // Pad base64 string if needed
        const padding = base64.length % 4;
        if (padding === 2) base64 += "==";
        else if (padding === 3) base64 += "=";
        else if (padding !== 0) {
            console.error("Invalid base64 string length");
            return null;
        }

        try {
            const byteString = window.atob(base64);
            const arrayBuffer = new ArrayBuffer(byteString.length);
            const intArray = new Uint8Array(arrayBuffer);
            for (let i = 0; i < byteString.length; i++) {
                intArray[i] = byteString.charCodeAt(i);
            }
            const blob = new Blob([intArray], { type: mimeType });
            return URL.createObjectURL(blob);
        } catch (error) {
            console.error("Failed to decode base64 audio", error);
            return null;
        }
    };


    //      const sendAudioMessage = async () => {
    //     if (!audioBlob || !selectedUser) return;

    //     try {
    //       const reader = new FileReader();
    //       reader.onloadend = async () => {
    //         const base64Audio = reader.result.split(",")[1];
    //         socket.emit("private_message", {
    //           senderId: userId,
    //           receiverId: selectedUser.id,
    //           content: base64Audio,
    //           type: "audio",
    //         });
    //       };
    //       reader.readAsDataURL(audioBlob);
    //       setAudioBlob(null);
    //     } catch (error) {
    //       console.error("Error sending audio message:", error);
    //     }
    //   };


    const sendAudioMessage = () => {
        if (!audioBlob || !socket || !socket.connected) return;

        const reader = new FileReader();
        reader.onloadend = () => {
            const base64Audio = reader.result.split(",")[1];

            if (isBroadcastMode) {
                const broadcastMessage = {
                    audio: base64Audio,
                    duration: recordingTime,
                    broadcast: true,
                };

                const tempMessage = {
                    _id: Date.now(),
                    audio: URL.createObjectURL(audioBlob), // For local playback
                    duration: recordingTime,
                    sender: { role: "admin", id: admin?._id },
                    receiver: { id: "broadcast" },
                    createdAt: new Date().toISOString(),
                    broadcast: true,
                };

                socket.emit("supportchat", broadcastMessage);
                setChatMessages((prev) => [...prev, tempMessage]);
            } else {
                if (!selectedUser) return;

                const newMessage = {
                    receiverId: selectedUser.userId,
                    receiverRole: "user",
                    audio: base64Audio,  // Use base64 instead of Blob
                    duration: recordingTime,
                };
                console.log("newMessage 1111", newMessage);

                const tempMessage = {
                    _id: Date.now(),
                    audio: URL.createObjectURL(audioBlob),
                    duration: recordingTime,
                    sender: { role: "admin", id: admin?._id },
                    receiver: { id: selectedUser.userId },
                    createdAt: new Date().toISOString(),
                };

                socket.emit("supportchat", newMessage);
                setChatMessages((prev) => [...prev, tempMessage]);
                handleNewInboxMessage(tempMessage);
            }

            setAudioBlob(null);
            setRecordingTime(0);
        };

        reader.readAsDataURL(audioBlob); // Convert Blob to base64 before sending
    };



    const playAudio = (audioId, audioUrl) => {
        const audioEl = audioRefs.current[audioId];
        if (!audioEl) return;

        if (playingAudioId === audioId) {
            audioEl.pause();
            audioEl.currentTime = 0;
            setPlayingAudioId(null);
        } else {
            if (playingAudioId && audioRefs.current[playingAudioId]) {
                audioRefs.current[playingAudioId].pause();
                audioRefs.current[playingAudioId].currentTime = 0;
            }
            audioEl.play().catch(err => {
            });
            setPlayingAudioId(audioId);
        }
    };


    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // Send message (updated for audio)
    const handleSendMessage = () => {
        if (!messageInput.trim() || !socket) return;

        const messageText = messageInput.trim();

        if (isBroadcastMode) {
            const broadcastMessage = {
                message: messageText,
                broadcast: true,
            };

            const tempMessage = {
                _id: Date.now(),
                message: messageText,
                sender: { role: "admin", id: admin?._id },
                receiver: { id: "broadcast" },
                createdAt: new Date().toISOString(),
                broadcast: true,
            };

            socket.emit("supportchat", broadcastMessage);
            setChatMessages((prev) => [...prev, tempMessage]);
        } else {
            if (!selectedUser) return;

            const newMessage = {
                receiverId: selectedUser.userId,
                receiverRole: "user",
                message: messageText,
            };

            const tempMessage = {
                _id: Date.now(),
                message: messageText,
                sender: { role: "admin", id: admin?._id },
                receiver: { id: selectedUser.userId },
                createdAt: new Date().toISOString(),
            };

            socket.emit("supportchat", newMessage);
            setChatMessages((prev) => [...prev, tempMessage]);
            handleNewInboxMessage(tempMessage);
        }

        setMessageInput("");
    };

    // --- UI rendering (unchanged) ---
    // Show authentication error
    if (authError) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4 }}>
                <Box
                    sx={{
                        p: 3,
                        border: "1px solid #f44336",
                        borderRadius: 2,
                        bgcolor: "#ffebee",
                        textAlign: "center",
                    }}
                >
                    <Typography variant="h6" color="error" gutterBottom>
                        🔐 Authentication Error
                    </Typography>
                    <Typography variant="body1" color="text.secondary" paragraph>
                        Unable to connect to the chat server. Please check your login status.
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            setAuthError(false);
                            // Optionally redirect to login
                            // window.location.href = '/login';
                        }}
                    >
                        Retry Connection
                    </Button>
                </Box>
            </Container>
        );
    }

    return (
        <Container
            maxWidth="lg"
            sx={{
                height: "85vh",
                border: "1px solid #ccc",
                borderRadius: 1,
                overflow: "hidden",
            }}
        >
            <Grid container sx={{ height: "100%" }}>
                {/* Left Inbox Panel */}
                {(!isMobile || !selectedUser) && (
                    <Grid
                        item
                        xs={12}
                        sm={4}
                        md={3}
                        sx={{
                            borderRight: { sm: "1px solid #ddd" },
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            overflow: "hidden",
                        }}
                    >
                        <Box sx={{ p: 2, borderBottom: "1px solid #ddd" }}>
                            <Typography variant="h6">Support Inbox</Typography>

                            {/* Search Input */}
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Search users..."
                                value={searchTerm}
                                onChange={handleSearch}
                                sx={{ mt: 1 }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            {/* Broadcast Button */}
                            <Tooltip title={isBroadcastMode ? "Exit Broadcast Mode" : "Enter Broadcast Mode"}>
                                <Button
                                    variant={isBroadcastMode ? "contained" : "outlined"}
                                    startIcon={<AnnouncementIcon />}
                                    onClick={toggleBroadcastMode}
                                    fullWidth
                                    sx={{
                                        mt: 1,
                                        position: "relative",
                                        ...(isBroadcastMode && {
                                            animation: "pulse 2s infinite",
                                            "@keyframes pulse": {
                                                "0%": { boxShadow: "0 0 0 0 rgba(255, 107, 53, 0.7)" },
                                                "70%": { boxShadow: "0 0 0 10px rgba(255, 107, 53, 0)" },
                                                "100%": { boxShadow: "0 0 0 0 rgba(255, 107, 53, 0)" }
                                            }
                                        })
                                    }}
                                    color={isBroadcastMode ? "warning" : "primary"}
                                >
                                    {isBroadcastMode ? "📢 Exit Broadcast" : "📢 Broadcast"}
                                </Button>
                            </Tooltip>


                        </Box>

                        {/* ... existing inbox list code ... */}
                        <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
                            {inboxLoading ? (
                                <Typography sx={{ p: 2 }}>Loading inbox...</Typography>
                            ) : inbox.length === 0 ? (
                                <Typography sx={{ p: 2 }}>
                                    {searchTerm ? "No users found" : "No conversations"}
                                </Typography>
                            ) : (
                                <List disablePadding>
                                    {inbox.map((conv) => {

                                        const user = conv?.userInfo;
                                        const isSelected = selectedUser?.userId === user?._id;
                                        const isBroadcastActive = isBroadcastMode;
                                        return (
                                            <ListItem
                                                button
                                                key={conv._id || conv.conversationKey}
                                                onClick={() => handleUserSelect(conv)}
                                                sx={{
                                                    bgcolor: isSelected ? "primary.light" : "transparent",
                                                    "&:hover": { bgcolor: "action.hover" },
                                                    cursor: "pointer",
                                                    px: 2,
                                                    py: 1.5,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "space-between",
                                                }}
                                            >
                                                <Box sx={{ display: "flex", alignItems: "center" }}>

                                                    <Avatar
                                                        src={user?.image ? `${IMAGE_BASE}${user?.image}` : undefined}
                                                        alt={capitalizeFirstLetter(user?.firstname)}
                                                        sx={{ width: 40, height: 40, mr: 2 }}
                                                    >
                                                        {user?.firstname?.[0]?.toUpperCase() || "U"}
                                                    </Avatar>
                                                    <ListItemText
                                                        primary={`${capitalizeFirstLetter(user?.firstname)} ${capitalizeFirstLetter(user?.lastname || "")}`}
                                                        secondary={
                                                            <Typography
                                                                component="span"
                                                                variant="body2"
                                                                color="text.primary"
                                                                noWrap
                                                            >
                                                                {conv.type == "text" ? conv.lastMessage : "Audio file"}
                                                            </Typography>
                                                        }
                                                    />
                                                </Box>

                                                {/* 🔔 Badge for unread messages */}
                                                {conv.unreadCount > 0 && (
                                                    <Box
                                                        sx={{
                                                            bgcolor: "green",
                                                            color: "white",
                                                            px: 1,
                                                            py: 0.2,
                                                            fontSize: "0.75rem",
                                                            borderRadius: "50%",
                                                            minWidth: 22,
                                                            height: 22,
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            fontWeight: "bold",
                                                        }}
                                                    >
                                                        {conv.unreadCount}
                                                    </Box>
                                                )}
                                            </ListItem>

                                        );
                                    })}
                                </List>
                            )}
                        </Box>

                        {/* ... existing pagination code ... */}
                        <Box
                            sx={{
                                p: 1,
                                borderTop: "1px solid #ddd",
                                mt: "auto",
                            }}
                        >
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Button
                                    size="small"
                                    disabled={inboxPage <= 1}
                                    onClick={() => setInboxPage((p) => Math.max(p - 1, 1))}
                                >
                                    Prev
                                </Button>
                                <Typography variant="caption">
                                    Page {inboxPage} of {inboxTotalPages}
                                </Typography>
                                <Button
                                    size="small"
                                    disabled={inboxPage >= inboxTotalPages}
                                    onClick={() => setInboxPage((p) => Math.min(p + 1, inboxTotalPages))}
                                >
                                    Next
                                </Button>
                            </Stack>
                        </Box>
                    </Grid>
                )}

                {/* Right Chat Panel */}
                {(!isMobile || selectedUser || isBroadcastMode) && (
                    <Grid
                        item
                        xs={12}
                        sm={8}
                        md={9}
                        sx={{
                            height: "100%",
                            bgcolor: "black",
                            display: "flex",
                            flexDirection: "column",
                            color: "white",
                        }}
                    >
                        <Box
                            sx={{
                                p: 2,
                                borderBottom: "1px solid #444",
                                fontWeight: "bold",
                                fontSize: 18,
                                height: 60,
                                display: "flex",
                                alignItems: "center",
                                color: "white",
                                backgroundColor: "#121212",
                            }}
                        >
                            {isMobile && (selectedUser || isBroadcastMode) && (
                                <Button
                                    onClick={() => {
                                        setSelectedUser(null);
                                        setIsBroadcastMode(false);
                                    }}
                                    sx={{
                                        color: "white",
                                        minWidth: "auto",
                                        mr: 2,
                                        fontSize: "1.2rem",
                                    }}
                                >
                                    ←
                                </Button>
                            )}
                            <Typography
                                variant="h6"
                                sx={{ fontWeight: "bold", fontSize: 18, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                            >
                                {isBroadcastMode
                                    ? "📢 Broadcast Mode - Send to All Users"
                                    : selectedUser
                                        ? `${capitalizeFirstLetter(selectedUser.firstname || selectedUser.username || "")} ${capitalizeFirstLetter(selectedUser.lastname || "")}`
                                        : "Select a user to chat"}
                            </Typography>
                        </Box>

                        {/* Messages List */}
                        <Box
                            ref={chatBoxRef}
                            sx={{
                                flexGrow: 1,
                                p: 2,
                                overflowY: "auto",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "flex-start",
                                gap: 1,
                                bgcolor: "#000",
                            }}
                        >
                            {chatLoading ? (
                                <Typography color="white">
                                    <Loader />
                                </Typography>
                            ) : chatMessages.length === 0 ? (
                                <Box
                                    sx={{
                                        flexGrow: 1,
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        color: "white",
                                        height: "100%",
                                    }}
                                >
                                    <Typography>
                                        {isBroadcastMode ? "No broadcast messages sent yet" : "No messages"}
                                    </Typography>
                                </Box>
                            ) : (
                                (() => {
                                    const groupedMessages = {};
                                    chatMessages.forEach((msg) => {
                                        const date = new Date(msg.createdAt).toLocaleDateString("en-GB");
                                        if (!groupedMessages[date]) groupedMessages[date] = [];
                                        groupedMessages[date].push(msg);
                                    });

                                    return Object.entries(groupedMessages).map(([date, messagesOnDate]) => (
                                        <React.Fragment key={date}>
                                            <Box sx={{ textAlign: "center", my: 2 }}>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        backgroundColor: "#333",
                                                        px: 2,
                                                        py: 0.5,
                                                        borderRadius: 10,
                                                        display: "inline-block",
                                                        color: "#ccc",
                                                    }}
                                                >
                                                    {date}
                                                </Typography>
                                            </Box>
                                            {messagesOnDate.map((msg) => {
                                                const isMe = msg.sender?.role === "admin";
                                                const isBroadcast = msg.broadcast || msg.receiver?.id === "broadcast";
                                                const isAudioMessage = msg.audio;

                                                return (
                                                    <Box
                                                        key={msg._id}
                                                        sx={{
                                                            maxWidth: "70%",
                                                            alignSelf: isMe ? "flex-end" : "flex-start",
                                                            bgcolor: isMe ? (isBroadcast ? "#ff6b35" : "white") : "grey.800",
                                                            color: isMe ? (isBroadcast ? "white" : "black") : "white",
                                                            borderRadius: 2,
                                                            px: 1.5,
                                                            py: 1,
                                                            wordBreak: "break-word",
                                                            display: "inline-block",
                                                            border: isBroadcast ? "2px solid #ff6b35" : "none",
                                                        }}
                                                    >
                                                        {isBroadcast && (
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    display: "block",
                                                                    fontWeight: "bold",
                                                                    mb: 0.5,
                                                                    color: isMe ? "white" : "#ff6b35",
                                                                }}
                                                            >
                                                                📢 BROADCAST
                                                            </Typography>
                                                        )}

                                                        {isAudioMessage ? (
                                                            // Audio Message
                                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 200 }}>
                                                                <audio
                                                                    ref={(el) => { if (el) audioRefs.current[msg._id] = el; }}
                                                                    onEnded={() => {
                                                                        setPlayingAudioId(null);
                                                                        setAudioProgress((prev) => ({ ...prev, [msg._id]: 0 }));
                                                                    }}
                                                                    onTimeUpdate={(e) => {
                                                                        const progress = (e.target.currentTime / e.target.duration) * 100;
                                                                        setAudioProgress((prev) => ({ ...prev, [msg._id]: progress }));
                                                                    }}
                                                                    autoPlay
                                                                    preload="auto"
                                                                    style={{ display: "none" }}
                                                                >
                                                                    <source src={msg.audio} type="audio/wav" />
                                                                </audio>

                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => playAudio(msg._id, msg.audio)}
                                                                    sx={{
                                                                        color: isMe ? (isBroadcast ? "white" : "black") : "white",
                                                                        bgcolor: isMe ? (isBroadcast ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.1)") : "rgba(255,255,255,0.1)"
                                                                    }}
                                                                >
                                                                    {playingAudioId === msg._id ? <PauseIcon /> : <PlayArrowIcon />}
                                                                </IconButton>

                                                                <Box sx={{ flexGrow: 1 }}>
                                                                    <Box sx={{
                                                                        width: "100%",
                                                                        height: 4,
                                                                        bgcolor: isMe ? (isBroadcast ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.2)") : "rgba(255,255,255,0.3)",
                                                                        borderRadius: 2,
                                                                        overflow: "hidden"
                                                                    }}>
                                                                        <Box
                                                                            sx={{
                                                                                width: `${audioProgress[msg._id] || 0}%`,
                                                                                height: "100%",
                                                                                bgcolor: isMe ? (isBroadcast ? "white" : "primary.main") : "white",
                                                                                transition: "width 0.1s linear"
                                                                            }}
                                                                        />

                                                                    </Box>
                                                                    <Typography variant="caption" sx={{ fontSize: "0.7rem", opacity: 0.8 }}>
                                                                        {formatTime(msg.duration || 0)}
                                                                    </Typography>
                                                                </Box>

                                                                <Typography variant="caption" sx={{ fontSize: "0.6rem", opacity: 0.6 }}>
                                                                    🎤
                                                                </Typography>
                                                            </Box>
                                                        ) : (
                                                            // Text Message
                                                            <Box
                                                                sx={{
                                                                    display: "flex",
                                                                    justifyContent: "space-between",
                                                                    alignItems: "flex-end",
                                                                    gap: 1,
                                                                    flexWrap: "wrap",
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        whiteSpace: "pre-line",
                                                                        flexGrow: 1,
                                                                        wordBreak: "break-word",
                                                                    }}
                                                                >
                                                                    {
                                                                        msg.type === "audio" && msg.message ? (
                                                                            <audio controls src={base64ToBlobUrl(msg.message, "audio/wav")} style={{ width: 200 }} />
                                                                        ) : (
                                                                            msg.message
                                                                        )
                                                                    }
                                                                </Typography>
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                        fontSize: "0.7rem",
                                                                        color: isMe ? (isBroadcast ? "rgba(255,255,255,0.8)" : "gray") : "#ccc",
                                                                        whiteSpace: "nowrap",
                                                                    }}
                                                                >
                                                                    {new Date(msg.createdAt).toLocaleTimeString([], {
                                                                        hour: "2-digit",
                                                                        minute: "2-digit",
                                                                    })}
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                    </Box>

                                                );
                                            })}

                                        </React.Fragment>
                                    ));
                                })()
                            )}
                        </Box>

                        {(selectedUser || isBroadcastMode) && (
                            <Box
                                sx={{
                                    p: 2,
                                    borderTop: "1px solid #444",
                                    display: "flex",
                                    gap: 1,
                                    alignItems: "flex-end",
                                }}
                            >
                                {/* Audio Recording UI */}
                                {isRecording ? (
                                    <Box sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        flexGrow: 1,
                                        bgcolor: "rgba(255,0,0,0.1)",
                                        p: 1,
                                        borderRadius: 2,
                                        border: "1px solid #ff4444"
                                    }}>
                                        <CircularProgress size={20} sx={{ color: "#ff4444" }} />
                                        <Typography variant="body2" sx={{ color: "#ff4444" }}>
                                            Recording... {formatTime(recordingTime)}
                                        </Typography>
                                        <IconButton
                                            size="small"
                                            onClick={stopRecording}
                                            sx={{ color: "#ff4444" }}
                                        >
                                            <StopIcon />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={cancelRecording}
                                            sx={{ color: "#666" }}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Box>
                                ) : audioBlob ? (
                                    <Box sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        flexGrow: 1,
                                        bgcolor: "rgba(0,255,0,0.1)",
                                        p: 1,
                                        borderRadius: 2,
                                        border: "1px solid #4caf50"
                                    }}>
                                        <Typography variant="body2" sx={{ color: "#4caf50" }}>
                                            🎤 {formatTime(recordingTime)}
                                        </Typography>
                                        <IconButton
                                            size="small"
                                            onClick={sendAudioMessage}
                                            sx={{ color: "#4caf50" }}
                                        >
                                            <SendIcon />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={cancelRecording}
                                            sx={{ color: "#666" }}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Box>
                                ) : (
                                    <>
                                        <TextField
                                            variant="outlined"
                                            placeholder={isBroadcastMode ? "Type broadcast message..." : "Type a message..."}
                                            value={messageInput}
                                            onChange={(e) => setMessageInput(e.target.value)}
                                            fullWidth
                                            size="small"
                                            InputProps={{
                                                sx: { bgcolor: "white", borderRadius: 1, color: "black" },
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter" && !e.shiftKey) {
                                                    e.preventDefault();
                                                    handleSendMessage();
                                                }
                                            }}
                                        />
                                        <IconButton
                                            onClick={startRecording}
                                            sx={{
                                                bgcolor: "primary.main",
                                                color: "white",
                                                "&:hover": { bgcolor: "primary.dark" }
                                            }}
                                        >
                                            <MicIcon />
                                        </IconButton>
                                        <Button
                                            variant="contained"
                                            onClick={handleSendMessage}
                                            color={isBroadcastMode ? "warning" : "primary"}
                                        >
                                            {isBroadcastMode ? "Broadcast" : "Send"}
                                        </Button>
                                    </>
                                )}
                            </Box>
                        )}
                    </Grid>
                )}
            </Grid>
        </Container>
    );
}
